// Phase T2.0A Prompt 2 — Admin event-metadata form fieldset.
//
// Reusable inside CreateTournamentDialog + CreateRoundRobinDialog + manage
// pages. Emits changes via `onChange({ ...currentValue, [key]: newValue })`.
// Handles date/time compaction: the two <input type="date"> and
// <input type="time"> controls are combined into a single ISO 8601
// timestamp before it's stored on `value.starts_at` / `value.ends_at`.
//
// Every field is optional — passing an empty string leaves the DB NULL.

import React from 'react';

// Split a stored ISO string back into local date + time inputs.
function isoToParts(iso) {
  if (!iso) return { date: '', time: '' };
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return { date: '', time: '' };
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
  } catch { return { date: '', time: '' }; }
}

// Combine local date + time into an ISO string in the browser's timezone.
function partsToIso(date, time) {
  if (!date && !time) return null;
  const d = date || new Date().toISOString().slice(0, 10);
  const t = time || '00:00';
  const local = new Date(`${d}T${t}`);
  if (Number.isNaN(local.getTime())) return null;
  return local.toISOString();
}

const label = 'text-[11px] uppercase tracking-wider text-[#6a766e]';
const input = 'mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm min-h-[44px] focus:outline-none focus:border-[#015e3f] focus:ring-2 focus:ring-[#015e3f]/20';

export default function EventDetailsFieldset({ value, onChange, hideDescription = false, structuredFormat = false }) {
  const startParts = isoToParts(value.starts_at);
  const endParts   = isoToParts(value.ends_at);
  const feeDollars = value.entry_fee_cents != null && value.entry_fee_cents !== ''
    ? (value.entry_fee_cents / 100).toString()
    : '';

  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-4" data-testid="event-details-fieldset">
      {!hideDescription && (
        <label className="block">
          <span className={label}>Description</span>
          <textarea
            data-testid="edf-description"
            rows={3}
            value={value.description || ''}
            onChange={(e) => set({ description: e.target.value })}
            placeholder="e.g. Grab a partner and join us for an afternoon of mixed doubles, food and drinks."
            className={`${input} resize-y`}
          />
        </label>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className={label}>Event date</span>
          <input
            data-testid="edf-date"
            type="date"
            value={startParts.date}
            onChange={(e) => set({ starts_at: partsToIso(e.target.value, startParts.time) })}
            className={input}
          />
        </label>
        <label className="block">
          <span className={label}>Start time</span>
          <input
            data-testid="edf-start-time"
            type="time"
            value={startParts.time}
            onChange={(e) => set({ starts_at: partsToIso(startParts.date, e.target.value) })}
            className={input}
          />
        </label>
      </div>

      <label className="block">
        <span className={label}>End time (optional)</span>
        <input
          data-testid="edf-end-time"
          type="time"
          value={endParts.time}
          onChange={(e) => set({ ends_at: partsToIso(startParts.date || endParts.date, e.target.value) })}
          className={input}
        />
      </label>

      <label className="block">
        <span className={label}>Location</span>
        <input
          data-testid="edf-location"
          type="text"
          value={value.location_text || ''}
          onChange={(e) => set({ location_text: e.target.value })}
          placeholder="e.g. The Rippers Club"
          className={input}
        />
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className={label}>Entry fee per player (USD)</span>
          <input
            data-testid="edf-entry-fee"
            type="number"
            step="0.01"
            min="0"
            value={feeDollars}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '') return set({ entry_fee_cents: null });
              const n = Number(v);
              if (Number.isFinite(n)) set({ entry_fee_cents: Math.round(n * 100) });
            }}
            placeholder="0.00"
            className={input}
          />
        </label>
        <label className="block">
          <span className={label}>Match format</span>
          {structuredFormat ? (
            <div
              data-testid="edf-match-format-readonly"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-[#e3dcd1] bg-[#f5f0e6] text-sm text-[#013523]"
            >
              <div className="font-medium">{value.match_format_text || '—'}</div>
              <div className="text-[10px] text-[#6a766e] mt-0.5">Generated from Format of Play. Change it in the Format of Play panel.</div>
            </div>
          ) : (
            <input
              data-testid="edf-match-format"
              type="text"
              value={value.match_format_text || ''}
              onChange={(e) => set({ match_format_text: e.target.value })}
              placeholder="e.g. Best 2 of 3 Sets · 10-Point Tiebreaker"
              className={input}
            />
          )}
        </label>
      </div>

      {/* Payments — Prompt 2.1. Payment TYPE + optional URL. No processing. */}
      <div className="border-t border-[#f3f1ec] pt-4 space-y-3" data-testid="edf-payments">
        <div className="text-[10px] uppercase tracking-wider text-[#013523] font-semibold">Payments</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className={label}>Payment method</span>
            <select
              data-testid="edf-payment-type"
              value={value.payment_type || ''}
              onChange={(e) => set({ payment_type: e.target.value || null })}
              className={input}
            >
              <option value="">— None —</option>
              <option value="zelle">Zelle</option>
              <option value="venmo">Venmo</option>
              <option value="cash">Cash</option>
              <option value="stripe">Stripe</option>
            </select>
          </label>
          {value.payment_type === 'stripe' ? (
            <div
              data-testid="edf-payment-stripe-hint"
              className="rounded-lg bg-[#f0e6cf]/40 border border-[#c9a93a]/40 px-3 py-2 text-[12.5px] text-[#3a2c05] self-end"
            >
              Members pay securely on the event page via Stripe Embedded Checkout. No payment link needed.
            </div>
          ) : (
            <label className="block">
              <span className={label}>Payment link (optional)</span>
              <input
                data-testid="edf-payment-link"
                type="url"
                value={value.payment_link || ''}
                onChange={(e) => set({ payment_link: e.target.value || null })}
                placeholder="https://venmo.com/u/…"
                className={input}
              />
            </label>
          )}
        </div>
      </div>
    </div>
  );
}
